/**
 * Browser logging setup utilities
 * Use this to initialize logging in UI applications (React, etc.)
 */
import { BrowserBackend } from "./backends/browser.js";
import { UnifiedLogger } from "./logger.js";
/**
 * Initialize logger for browser/UI applications
 *
 * Features:
 * - Automatic error tracking (unhandled errors and promise rejections)
 * - Production mode: Only errors/warnings sent to Better Stack
 * - Development mode: All logs shown in console
 * - Graceful degradation if Better Stack token not provided
 *
 * @example
 * ```typescript
 * // In your main.tsx or App.tsx
 * import { initializeBrowserLogger } from "@agent-todo/shared/logging"
 *
 * // Initialize before rendering app
 * initializeBrowserLogger({
 *   serviceName: "agent-todo-ui",
 *   betterStackToken: import.meta.env.VITE_BETTER_STACK_LOGS_TOKEN,
 *   environment: import.meta.env.MODE,
 * })
 *
 * // Then use logger anywhere in your app
 * import { logger } from "@agent-todo/shared/logging"
 * logger.error("Failed to fetch tasks", error)
 * ```
 */
export function initializeBrowserLogger(config) {
    const loggerInstance = UnifiedLogger.getInstance();
    // Reinitialize logger (clears old backends)
    loggerInstance.initialize({
        serviceName: config.serviceName,
        environment: config.environment ?? "production",
    });
    // Add browser backend for remote logging (only errors/warnings in production)
    if (config.betterStackToken) {
        // In development, enable all log levels if requested
        const enableAllLevels = config.environment === "development";
        const options = {
            enableAllLevels,
            ...(config.sentryDsn !== undefined && { endpoint: config.sentryDsn }),
        };
        const browserBackend = new BrowserBackend(config.betterStackToken, options);
        loggerInstance.backends.push(browserBackend);
    }
    // Console backend is always added by initialize() as fallback
    // Set up automatic error tracking
    setupErrorTracking(loggerInstance);
    // Set up page unload handler to flush logs
    setupUnloadHandler(loggerInstance);
    return loggerInstance;
}
/**
 * Set up automatic error tracking
 * Captures unhandled errors and promise rejections
 */
function setupErrorTracking(logger) {
    // Capture unhandled errors
    window.addEventListener("error", (event) => {
        logger.error("Unhandled error", event.error ?? new Error(event.message), {
            operation: "window.error",
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
        // Prevent default browser error handling
        // Remove this if you want the browser's default error UI
        // event.preventDefault()
    });
    // Capture unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
        // The View Transitions API rejects the transition promise with
        // "Transition was aborted because of invalid state" (InvalidStateError)
        // when a transition is superseded or the tab isn't active. It's benign
        // browser noise, not an application error — swallow it rather than flood
        // error tracking. See React #34098.
        const reason = event.reason;
        if (reason?.name === "InvalidStateError" && reason.message?.includes("Transition was aborted")) {
            event.preventDefault();
            return;
        }
        logger.error("Unhandled promise rejection", event.reason instanceof Error ? event.reason : new Error(String(event.reason)), {
            operation: "window.unhandledrejection",
        });
        // Prevent default browser error handling
        // event.preventDefault()
    });
}
/**
 * Set up page unload handler to flush pending logs
 * Ensures logs are sent before the page closes
 */
function setupUnloadHandler(logger) {
    window.addEventListener("beforeunload", () => {
        // Flush logs synchronously (use void to ignore promise)
        void logger.flush();
    });
}
/**
 * Create a logger with user context
 * Use this after user logs in to attach user info to all logs
 *
 * @example
 * ```typescript
 * // After login
 * const userLogger = createUserLogger({ userId: user.id, userEmail: user.email })
 * userLogger.info("User action") // Automatically includes userId and userEmail
 * ```
 */
export function createUserLogger(context) {
    const logger = UnifiedLogger.getInstance();
    return logger.withContext(context);
}
//# sourceMappingURL=browser-setup.js.map